import { useState } from 'react';
import type { ReactElement } from 'react';
import { assetToast, formatCurrency, formatDate } from '@xdocs/i18n';
import type { PromoCode } from '@xdocs/schemas';
import { Badge, Button, ConfirmDialog, DataTable, cn, useI18n, useToast } from '@xdocs/ui';
import type { BadgeTone, ColumnDef } from '@xdocs/ui';
import { requestTyped } from '@xdocs/ws-protocol';
import { RoleGate } from '../../../features/admin/index.js';
import { safeT } from '../../../i18n/safeT.js';
import { optionalProp } from '../../../lib/optionalProp.js';
import { useWs } from '../../../ws/WsProvider.js';
import { PromoFormDialog } from './PromoFormDialog.js';
import { usePromoList } from './usePromoList.js';
import { usePromosT } from './usePromosT.js';
import styles from './PromosPage.module.css';

const KIND_TONE: Record<PromoCode['kind'], BadgeTone> = {
  percent_off: 'info',
  fixed_off: 'info',
  bonus_credits: 'credits',
};

function formatValue(locale: 'en' | 'ro', promo: PromoCode): string {
  if (promo.kind === 'percent_off') return `${promo.valueNumeric}%`;
  if (promo.kind === 'bonus_credits') return `+${promo.valueNumeric} cr`;
  return formatCurrency(locale, promo.valueNumeric, promo.currency ?? 'USD');
}

/**
 * `/admin/promos` (F55) — promo-code catalog CRUD on the shared
 * `<DataTable>` (E4 §8.3) + create/edit form dialog + deactivate confirm.
 * Redemption drill-down is DISABLED with a `disabledReason` (ux.md: never a
 * dead greyed control with no explanation) — no
 * `admin.promo.redemptions.list` op exists in the frozen WS contracts yet
 * (see this task's returned open-issues list).
 *
 * Wrapped in `<RoleGate minimum="admin">` (`features/admin/`, sibling M4
 * task): F55 is `admin`-only, stricter than `AdminLayout`'s own
 * `minimum='moderator'` shell gate — same defense-in-depth pattern
 * `AdminLayout.tsx` prescribes for its own admin-only pages. UX-only; the
 * real authorization is server-side on every `admin.*` op regardless.
 */
export function PromosPage(): ReactElement {
  const { locale } = useI18n();
  const { t } = usePromosT();
  const { wsClient } = useWs();
  const toast = useToast();
  const { rows, total, nextCursor, query, onQueryChange, onLoadMore, isLoading, isLoadingMore, isError, loadMoreError, onRetry, invalidate } =
    usePromoList();

  const [formOpen, setFormOpen] = useState(false);
  const [editingPromo, setEditingPromo] = useState<PromoCode | null>(null);
  const [deactivateTarget, setDeactivateTarget] = useState<PromoCode | null>(null);
  const [deactivateError, setDeactivateError] = useState<string | null>(null);
  const [isDeactivating, setIsDeactivating] = useState(false);

  function openCreate(): void {
    setEditingPromo(null);
    setFormOpen(true);
  }

  function openEdit(promo: PromoCode): void {
    setEditingPromo(promo);
    setFormOpen(true);
  }

  function handleSaved(promo: PromoCode): void {
    setFormOpen(false);
    toast.push(assetToast(locale, 'success', t(editingPromo ? 'adminPromos:toast.updated.action' : 'adminPromos:toast.created.action'), promo.code));
    invalidate();
  }

  async function handleDeactivate(): Promise<void> {
    if (!deactivateTarget) return;
    setIsDeactivating(true);
    setDeactivateError(null);
    try {
      await requestTyped(wsClient, 'admin.promo.deactivate', { promoCodeId: deactivateTarget.id });
      toast.push(assetToast(locale, 'success', t('adminPromos:toast.deactivated.action'), deactivateTarget.code));
      invalidate();
      setDeactivateTarget(null);
    } catch (err) {
      setDeactivateError(safeT(locale, (err as { message?: string }).message ?? 'errors:code.INTERNAL'));
    } finally {
      setIsDeactivating(false);
    }
  }

  const columns: ColumnDef<PromoCode>[] = [
    {
      id: 'code',
      header: t('adminPromos:list.column.code'),
      primary: true,
      cell: (row) => <span className={cn(styles['codeCell'])}>{row.code}</span>,
    },
    {
      id: 'kind',
      header: t('adminPromos:list.column.kind'),
      cell: (row) => <Badge tone={KIND_TONE[row.kind]}>{t(`adminPromos:kind.${row.kind}`)}</Badge>,
    },
    {
      id: 'value',
      header: t('adminPromos:list.column.value'),
      cell: (row) => <span>{formatValue(locale, row)}</span>,
    },
    {
      id: 'redemptionCount',
      header: t('adminPromos:list.column.redemptions'),
      sortable: true,
      cell: (row) => <span>{row.redemptionCount}{row.maxRedemptions !== null ? ` / ${row.maxRedemptions}` : ''}</span>,
    },
    {
      id: 'validUntil',
      header: t('adminPromos:list.column.validity'),
      sortable: true,
      hideOnMobile: true,
      cell: (row) => (
        <span className={cn(styles['metaCell'])}>
          {row.validUntil ? formatDate(locale, row.validUntil, { dateStyle: 'medium' }) : t('adminPromos:validity.noEnd')}
        </span>
      ),
    },
    {
      id: 'status',
      header: t('adminPromos:list.column.status'),
      cell: (row) => <Badge tone={row.isActive ? 'success' : 'neutral'}>{t(row.isActive ? 'adminPromos:status.active' : 'adminPromos:status.inactive')}</Badge>,
    },
    {
      id: 'actions',
      header: '',
      cell: (row) => (
        <div className={cn(styles['actionsCell'])}>
          <Button variant="ghost" size="sm" onClick={() => openEdit(row)}>
            {t('adminPromos:action.edit')}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            disabled
            disabledReason={t('adminPromos:action.viewRedemptions.disabledReason')}
          >
            {t('adminPromos:action.viewRedemptions')}
          </Button>
          {row.isActive && (
            <Button variant="ghost" size="sm" onClick={() => setDeactivateTarget(row)}>
              {t('adminPromos:action.deactivate')}
            </Button>
          )}
        </div>
      ),
    },
  ];

  return (
    <RoleGate minimum="admin">
    <div className={cn(styles['page'])}>
      <div className={cn(styles['header'])}>
        <div>
          <h1 className={cn(styles['title'])}>{t('adminPromos:page.title')}</h1>
          <p className={cn(styles['description'])}>{t('adminPromos:page.description')}</p>
        </div>
        <Button variant="primary" onClick={openCreate}>
          {t('adminPromos:action.create')}
        </Button>
      </div>

      <DataTable
        columns={columns}
        rows={rows}
        rowKey={(row) => row.id}
        query={query}
        onQueryChange={onQueryChange}
        label={t('adminPromos:page.title')}
        isLoading={isLoading}
        isError={isError}
        onRetry={onRetry}
        total={total}
        nextCursor={nextCursor}
        onLoadMore={onLoadMore}
        isLoadingMore={isLoadingMore}
        {...optionalProp('loadMoreError', loadMoreError)}
        emptyTitle={t('adminPromos:list.empty.title')}
        emptyDescription={t('adminPromos:list.empty.description')}
        emptyAction={{ label: t('adminPromos:action.create'), onClick: openCreate }}
        noResultsTitle={t('adminPromos:list.noResults.title', { query: query.q ?? '' })}
        onClearFilters={() => {
          const { q: _q, ...rest } = query;
          onQueryChange({ ...rest, page: 1 });
        }}
        searchLabel={t('adminPromos:list.searchLabel')}
        searchPlaceholder={t('adminPromos:list.searchPlaceholder')}
      />

      {formOpen && (
        <PromoFormDialog editingPromo={editingPromo} open={formOpen} onClose={() => setFormOpen(false)} onSaved={handleSaved} />
      )}

      <ConfirmDialog
        open={deactivateTarget !== null}
        onOpenChange={(open) => !open && setDeactivateTarget(null)}
        action={t('adminPromos:confirm.deactivate.action')}
        assetName={deactivateTarget?.code ?? ''}
        destructive
        loading={isDeactivating}
        {...optionalProp('error', deactivateError ?? undefined)}
        onConfirm={() => void handleDeactivate()}
      />
    </div>
    </RoleGate>
  );
}
